// Persist the GitHub App setup params (installation_id, setup_action, state) across the sign-in
// round-trip. GitHub sends users to the new-project screen with ?installation_id=… after installing the
// App, but a logged-out visitor has to bounce through Auth0 to sign in first. The query string is supposed to
// survive that round-trip (returnTo → OAuth state → callback redirect), but in practice it can get
// dropped — leaving the page with no installation to link. We stash the params in localStorage right
// before bouncing to login and restore them when the user lands back on the page signed in.
//
// The stash is timestamped and treated as a short-lived, single-use handoff: it's ignored (and
// cleared) once it's older than TTL_MS, so a stale entry can never silently re-inject an old
// installation into a future, unrelated visit.

const KEY = 'replay:github-setup-params'

// The stash only has to survive one sign-in redirect. Anything older is stale — drop it.
const TTL_MS = 10 * 60 * 1000

// Only the params that identify the installation — never auth noise (code/state from OAuth).
const PERSISTED_KEYS = ['installation_id', 'setup_action', 'state'] as const

interface StoredSetupParams {
  params: string // a URL search string with no leading "?"
  savedAt: number // epoch ms
}

function safeLocal(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

// Save the install-identifying params from a URL search string (e.g. window.location.search).
export function saveGithubSetupParams(search: string): void {
  const ls = safeLocal()
  if (!ls) return
  const from = new URLSearchParams(search)
  const keep = new URLSearchParams()
  for (const k of PERSISTED_KEYS) {
    const v = from.get(k)
    if (v) keep.set(k, v)
  }
  const params = keep.toString()
  if (!params) return
  const entry: StoredSetupParams = { params, savedAt: Date.now() }
  try {
    ls.setItem(KEY, JSON.stringify(entry))
  } catch {
    /* ignore quota/serialization errors */
  }
}

// Read and clear any saved params. Returns a search string (no leading "?") or null. Entries older
// than TTL_MS are discarded as stale.
export function takeGithubSetupParams(): string | null {
  const ls = safeLocal()
  if (!ls) return null
  const raw = ls.getItem(KEY)
  if (!raw) return null
  ls.removeItem(KEY)
  try {
    const entry = JSON.parse(raw) as Partial<StoredSetupParams>
    if (typeof entry.params !== 'string' || typeof entry.savedAt !== 'number') return null
    if (Date.now() - entry.savedAt > TTL_MS) return null
    return entry.params || null
  } catch {
    return null
  }
}

// Where the user was when they left to set up the GitHub App. When a project's GitHub tab sends the
// user off to github.com to install the App, GitHub's post-install redirect drops them on the generic
// new-project screen with no idea which project they came from. We stash the originating in-app path
// here right before bouncing to GitHub, and the landing sends them back to it — the same localStorage
// handoff used for the install params above, with the same single-use, TTL'd stash. localStorage (not
// session) on purpose: this install can open in another tab, and the return still has to find it.
const RETURN_KEY = 'replay:github-setup-return'

interface StoredReturnPath {
  path: string // an in-app path like "/projects/abc/github"
  savedAt: number // epoch ms
}

// Read and clear any saved return path. Returns an in-app path (with leading "/") or null. Entries
// older than TTL_MS are discarded as stale.
export function takeGithubSetupReturnPath(): string | null {
  const ls = safeLocal()
  if (!ls) return null
  const raw = ls.getItem(RETURN_KEY)
  if (!raw) return null
  ls.removeItem(RETURN_KEY)
  try {
    const entry = JSON.parse(raw) as Partial<StoredReturnPath>
    if (typeof entry.path !== 'string' || typeof entry.savedAt !== 'number') return null
    if (Date.now() - entry.savedAt > TTL_MS) return null
    // Re-validate on the way out — never hand back anything but a same-origin in-app path.
    if (!entry.path.startsWith('/') || entry.path.startsWith('//')) return null
    return entry.path || null
  } catch {
    return null
  }
}
